import { ref, onMounted } from 'vue';
import axios from 'axios';
import { authState } from '@/utils/auth';
import { apiErrorMessage } from '@/utils/apiError';

export const CHART_DATA_KEY = 'chartData';
export const CHART_BIRTH_DATA_KEY = 'chartBirthData';
export const CHART_PERSISTENCE_WARNING_KEY = 'chartPersistenceWarning';

function readJsonStorage(key) {
  const raw = sessionStorage.getItem(key);
  if (!raw) {
    return null;
  }
  try {
    return JSON.parse(raw);
  } catch (e) {
    console.warn(`useChartStatus: failed to parse sessionStorage ${key}`, e);
    return null;
  }
}

function writeJsonStorage(key, value) {
  if (value == null) {
    sessionStorage.removeItem(key);
    return;
  }
  sessionStorage.setItem(key, JSON.stringify(value));
}

export function clearPersistenceWarning() {
  sessionStorage.removeItem(CHART_PERSISTENCE_WARNING_KEY);
}

export function clearPersonalChartSession() {
  sessionStorage.removeItem(CHART_DATA_KEY);
  sessionStorage.removeItem(CHART_BIRTH_DATA_KEY);
  clearPersistenceWarning();
}

function readAndClearPersistenceWarning() {
  const warning = sessionStorage.getItem(CHART_PERSISTENCE_WARNING_KEY) || '';
  sessionStorage.removeItem(CHART_PERSISTENCE_WARNING_KEY);
  return warning;
}

export function birthLocationKey(birthData) {
  if (!birthData) {
    return '';
  }
  const lat = birthData.lat;
  const lon = birthData.long;
  if (lat != null && lon != null) {
    return `${Number(lat).toFixed(4)},${Number(lon).toFixed(4)}`;
  }
  return [birthData.city, birthData.region, birthData.country].filter(Boolean).join('|');
}

export function birthLocationsMatch(a, b) {
  if (!a || !b) {
    return false;
  }
  const keyA = birthLocationKey(a);
  const keyB = birthLocationKey(b);
  if (keyA && keyB) {
    return keyA === keyB;
  }
  return false;
}

export function stripChartPayload(raw) {
  if (!raw || typeof raw !== 'object') {
    return raw;
  }
  const {
    birth_data: _birthData,
    persisted: _persisted,
    persistence_error: _persistenceError,
    ...chartPayload
  } = raw;
  return chartPayload;
}

export function cachePersonalChart(chartData, birthData) {
  writeJsonStorage(CHART_DATA_KEY, stripChartPayload(chartData));
  if (birthData == null) {
    sessionStorage.removeItem(CHART_BIRTH_DATA_KEY);
    return;
  }
  writeJsonStorage(CHART_BIRTH_DATA_KEY, birthData);
}

export function useChartStatus() {
  const loading = ref(true);
  const hasChart = ref(false);
  const isSavedChart = ref(false);
  const chartData = ref(null);
  const birthData = ref(null);
  const loadError = ref('');
  const persistenceWarning = ref('');

  function applyLoadedChart(chart, birth, { saved = false } = {}) {
    const cleanChart = stripChartPayload(chart);
    chartData.value = cleanChart;
    birthData.value = birth ?? null;
    hasChart.value = Boolean(cleanChart);
    isSavedChart.value = saved;
    if (saved) {
      clearPersistenceWarning();
      persistenceWarning.value = '';
    }
    cachePersonalChart(cleanChart, birth ?? null);
  }

  async function fetchSavedChartFromApi() {
    const response = await axios.get('/api/v1/skyview/my_chart');
    const chart = response.data.chart_data ?? null;
    const birth = response.data.birth_data ?? null;
    if (chart) {
      applyLoadedChart(chart, birth, { saved: true });
    }
    return { chart, birth };
  }

  async function load() {
    loading.value = true;
    loadError.value = '';
    hasChart.value = false;
    isSavedChart.value = false;
    chartData.value = null;
    birthData.value = null;
    persistenceWarning.value = '';

    const pendingWarning = readAndClearPersistenceWarning();
    const storedChart = readJsonStorage(CHART_DATA_KEY);
    const storedBirth = readJsonStorage(CHART_BIRTH_DATA_KEY);

    try {
      if (authState.isAuthenticated) {
        try {
          const { chart: apiChart, birth: apiBirth } = await fetchSavedChartFromApi();
          if (
            pendingWarning &&
            storedChart &&
            storedBirth &&
            !birthLocationsMatch(storedBirth, apiBirth)
          ) {
            applyLoadedChart(storedChart, storedBirth, { saved: false });
            persistenceWarning.value = pendingWarning;
          } else if (apiChart) {
            persistenceWarning.value = '';
          }
        } catch (err) {
          if (err?.response?.status === 404) {
            if (storedChart) {
              applyLoadedChart(storedChart, storedBirth, { saved: false });
              persistenceWarning.value = pendingWarning;
            }
          } else {
            loadError.value = apiErrorMessage(err, 'Could not load your saved chart.');
            if (storedChart) {
              applyLoadedChart(storedChart, storedBirth, { saved: false });
              persistenceWarning.value = pendingWarning;
            }
          }
        }
      } else if (storedChart) {
        applyLoadedChart(storedChart, storedBirth, { saved: false });
        persistenceWarning.value = pendingWarning;
      }
    } finally {
      loading.value = false;
    }
  }

  onMounted(load);

  return {
    loading,
    hasChart,
    isSavedChart,
    chartData,
    birthData,
    loadError,
    persistenceWarning,
    load,
    cachePersonalChart,
    clearPersistenceWarning,
  };
}
